import { styled } from '@mui/material/styles';
import RadioGroup, { useRadioGroup } from '@mui/material/RadioGroup';
import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { FormControl, FormHelperText, FormLabel } from '@mui/material';
import { useField } from 'formik';
import { ChangeEvent } from 'react';

interface StyledFormControlLabelProps extends FormControlLabelProps {
  checked: boolean;
}

const StyledFormControlLabel = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ theme, checked }) => ({
  paddingRight: '10px',
  borderRadius: '12px',
  backgroundColor: ' #ecf5ff',
  border: '1px solid transparent',
  color: ' #656f7a',
  marginLeft: 0,
  marginBottom: '10px',
  '&': checked && {
    backgroundColor: 'rgba(0, 87, 155, 0.0535)',
    border: '1px solid #00579B',
  },
  '.MuiFormControlLabel-label': {
    fontWeight: 500,
    fontSize: '14px',
    '&': checked && {
      color: theme.palette.primary.main,
    },
  },
}));

const PillFormControlLabel = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ checked }) => ({
  marginLeft: 0,
  marginRight: 12,
  marginBottom: 0,
  padding: '8px 20px',
  borderRadius: '999px',
  border: checked ? '1px solid #2057A6' : '1px solid #E5E7EB',
  backgroundColor: '#FFFFFF',
  color: checked ? '#2057A6' : '#4B5563',
  '.MuiRadio-root': {
    display: 'none',
  },
  '.MuiFormControlLabel-label': {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    color: checked ? '#2057A6' : '#4B5563',
  },
}));

function MyFormControlLabel({
  appearance,
  ...props
}: FormControlLabelProps & { appearance?: 'default' | 'pills' }) {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  if (appearance === 'pills') {
    return <PillFormControlLabel checked={checked} {...props} />;
  }

  return <StyledFormControlLabel checked={checked} {...props} />;
}

export default function UseRadioGroup({
  name,
  formLabel,
  defaultValue,
  items,
  labelColor = 'rgba(38, 48, 60, 0.7)',
  value = '',
  disabled = false,
  onChange,
  appearance = 'default',
}: {
  name: string;
  formLabel?: string;
  defaultValue?: string | boolean;
  labelColor?: string;
  items: { label: string; value: string | boolean | number; disabled?: boolean }[];
  value?: boolean | string | undefined | number;
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  appearance?: 'default' | 'pills';
}): JSX.Element {
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : '';
  const errorMessageId = `${name}-error`;

  const fieldValue = value ? value : field.value;
  onChange = onChange || field.onChange;

  return (
    <FormControl component="fieldset" sx={{ width: '100%' }}>
      {formLabel && (
        <FormLabel
          component="legend"
          sx={{
            mb: appearance === 'pills' ? 1.25 : 2,
            fontWeight: 500,
            fontSize: 14,
            color: labelColor,
            fontFamily: 'Inter, sans-serif',
          }}>
          {formLabel}
        </FormLabel>
      )}
      <RadioGroup
        row
        defaultValue={defaultValue}
        {...field}
        onChange={onChange}
        value={fieldValue}
        sx={{
          '& .Mui-disabled': {
            pointerEvents: 'all',
          },
        }}>
        {items.map((item, index) => (
          <MyFormControlLabel
            key={index}
            appearance={appearance}
            label={item.label}
            value={item.value}
            disabled={item.disabled || disabled}
            control={<Radio disableRipple size="small" sx={{ ':hover': { background: 'none' } }} />}
            sx={{ cursor: !item.disabled || !disabled ? 'pointer' : 'not-allowed !important' }}
          />
        ))}
      </RadioGroup>
      {errorText && (
        <FormHelperText
          error
          sx={{ marginLeft: 'unset' }}
          id={errorMessageId}
          role="alert"
          aria-live="assertive"
          aria-atomic="true">
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
